/* ManageDeviceViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageDeviceViewRenderer");
const PagingDeviceFeed = require("./PagingDeviceFeed");
const _ = require("lodash");

const DeviceTypeColumn = require("./column_renderers/device_type/DeviceTypeComponent");

class ManageDeviceViewComponent extends EventEmitter {
    constructor(planDetails) {
        super();
        this._planDetails = planDetails;
        this._planDetails.childViewOpen = true;
        this._gameEditor = planDetails.gameEditor;
        this._deviceEntityDef = planDetails._deviceEntityDef;
        this._modalUserFeedback = planDetails._modalUserFeedback;
        this._pageSize = 100;
        this._gridKey = "seeded_device";
        this._logger = planDetails._logger;
        this._isDirty = false;
        this._selectedIDs = undefined;
        this._userGridData = undefined;
        this._deviceFeedSystemQuery = {managementState: {$in: ["MANAGED"]}};
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    shutdown() {
        return Promise.all([this._availableDevicesPagingFeed.shutdown()])
            .then(() => {
                this.emit("Shutdown");
                this._unregisterListeners();
                this._availableDevicesPagingFeed = undefined;
                this._selectedIDs = undefined;
                Promise.resolve();
            })
            .catch((err) => {
                console.error(err);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get isDirty() {
        return this._isDirty;
    }

    set isDirty(newValue) {
        this._isDirty = newValue;
        return true;
    }
    get selectedIDs() {
        return this._selectedIDs;
    }

    set selectedIDs(newValue) {
        this._selectedIDs = newValue;
        return true;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get getPlanDetailsView() {
        return this._plan;
    }

    get availColumnConfig() {
        const typeBaseMap = this._typeBaseMap;
        const optionMapDeviceTypes = {};
        _.sortBy(Object.keys(typeBaseMap)).map(function (key) {
            optionMapDeviceTypes[key] = {
                name: typeBaseMap[key],
                title: typeBaseMap[key],
            };
        });

        return {
            detectedTypeBase: {
                label: "Type",
                columnRendererComponent: DeviceTypeColumn,
                entityColumn: true,
                width: 45,
                resizable: false,
                priority: 1,
                options: optionMapDeviceTypes,
                groupable: true,
                shown: true,
            },
            name: {
                label: "Name",
                entityColumn: "name",
                width: 200,
                priority: 2,
            },
            description: {
                label: "Description",
                entityColumn: "description",
                width: 150,
                priority: 3,
            },
            detectedHostName: {
                label: "Hostname",
                shown: true,
                priority: 4,
                width: 140,
            },
            ip: {
                label: "IP Address",
                path: "connectionInformation.ip",
                shown: true,
                width: 140,
                priority: 5,
            },
            detectedVendor: {
                label: "Vendor",
                groupable: true,
                shown: true,
                priority: 6,
                width: 140,
                options: Object.entries(this._vendorMap).map(([, title]) => ({name: title, title})),
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                shown: true,
                priority: 7,
                width: 140,
                groupable: true,
            },
            detectedSerialNumber: {
                label: "Serial",
                shown: true,
                groupable: true,
                width: 140,
                priority: 8,
            },
            detectedSku: {
                label: "SKUs",
                entityColumn: "detectedSku",
                width: 100,
                priority: 9,
            },
        };
    }

    get availableTargetsPagingFeed() {
        return this._availableDevicesPagingFeed;
    }
    get selectedTargetsPagingFeed() {
        return this._selectedDevicesPagingFeed;
    }
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    wasFeedPreviouslyInitialized() {
        return this._availableDevicesPagingFeed.wasPreviouslyInitialized();
    }

    whenModelIsReady() {
        return this._getTypeBaseMapDynamic()
            .then(() => this._getVendorMapDynamic())
            .then(() => this._createAvailPagingFeed())
            .then(() => {
                return Promise.all([this._availableDevicesPagingFeed.whenFeedReady()]);
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this.shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _createAvailPagingFeed() {
        if (this._availableDevicesPagingFeed) {
            this._availableDevicesPagingFeed.shutdown();
        }
        this._availableDevicesPagingFeed = this._createPagingFeed(
            this.availColumnConfig,
            "$nin",
            `${this._gridKey}_available`,
            true
        );
        return Promise.resolve();
    }

    async _getTypeBaseMapDynamic() {
        this._typeBaseMap = await this._deviceEntityDef.loadAndMergeTypeBaseMap(
            this._gameEditor.activeOrgId
        );
        return Promise.resolve();
    }

    async _getVendorMapDynamic() {
        this._vendorMap = await this._deviceEntityDef.loadAndMergeVendorMap(
            this._gameEditor.activeOrgId
        );
        return Promise.resolve();
    }

    _createPagingFeed(columnConfig, mongoIdOperator, feedKey) {
        return new PagingDeviceFeed({
            activeOrgId: this._gameEditor.activeOrgId,
            entityDef: this._deviceEntityDef,
            entityType: "Device",
            columnConfig: columnConfig,
            pageSize: this._pageSize,
            mongoIdOperator: mongoIdOperator,
            sort: {name: 1},
            filter: {},
            systemQuery: this._deviceFeedSystemQuery,
            feedKey,
        });
    }
    _saveDevice() {
        return Promise.resolve()
            .then(() => {})
            .catch((err) => {
                this._logger.warn({error: err}, "ManageDeviceViewComponent.saveDevice() error.");
                this._modalUserFeedback.displayError(err);
                return Promise.reject();
            });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = ManageDeviceViewComponent;
